import React, { useState, useMemo } from "react";
import { List, useTable, ShowButton, EditButton, DeleteButton } from "@refinedev/antd";
import { Table, Space, Typography, Card, Row, Col, Input, Button, Tag, Statistic } from "antd";
import { SearchOutlined, UserOutlined, EuroOutlined, CalendarOutlined, PlusOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";
import { IGuest } from "../../interfaces";
import { useHashNavigation } from "../../hooks/useHashNavigation";
import { useTypo3Api } from "../../services/typo3Service";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Search } = Input;

export const GuestList: React.FC = () => {
  const { navigateWithHash } = useHashNavigation();
  const [searchText, setSearchText] = useState<string>("");

  const { tableProps, searchFormProps } = useTable<IGuest>({
    resource: "guests",
    onSearch: (params: any) => {
      return [
        {
          field: "name",
          operator: "contains",
          value: params.q,
        },
      ];
    },
  });

  const { getGuestStatistics } = useTypo3Api();
  const [statistics, setStatistics] = React.useState<any>(null);

  React.useEffect(() => {
    getGuestStatistics().then(response => {
      setStatistics(response.data);
    }).catch(console.error);
  }, []);

  // Frontend-Suchfunktion
  const filteredData = useMemo(() => {
    if (!searchText || !tableProps.dataSource) {
      return tableProps.dataSource || [];
    }

    const searchLower = searchText.toLowerCase();
    return (tableProps.dataSource as IGuest[]).filter((guest: IGuest) => {
      const searchableFields = [
        guest.name,
        guest.firstname,
        guest.lastname,
        guest.email,
        guest.mobile,
        guest.phone,
        guest.city,
        guest.company
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchLower)
      );
    });
  }, [tableProps.dataSource, searchText]);

  const columns = [
    {
      title: "Name",
      key: "fullname",
      width: 180,
      render: (_: any, record: IGuest) => (
        <Space direction="vertical" size={0}>
          <Text strong>
            {record.lastname || record.name?.split(' ').pop() || ''}, {record.firstname || record.name?.split(' ')[0] || ''}
          </Text>
          {record.company && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {record.company}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "E-Mail",
      dataIndex: "email",
      key: "email",
      width: 200,
      render: (value: string) => (
        <Space>
          <MailOutlined style={{ color: '#1890ff' }} />
          <Text style={{ fontSize: '12px' }}>{value || '-'}</Text>
        </Space>
      ),
    },
    {
      title: "Mobile/Telefon",
      key: "phone_contact",
      width: 140,
      render: (_: any, record: IGuest) => (
        <Space direction="vertical" size={0}>
          {record.mobile && (
            <Space size={4}>
              <PhoneOutlined style={{ color: '#52c41a' }} />
              <Text style={{ fontSize: '12px' }}>{record.mobile}</Text>
            </Space>
          )}
          {record.phone && record.phone !== record.mobile && (
            <Space size={4}>
              <PhoneOutlined style={{ color: '#722ed1' }} />
              <Text style={{ fontSize: '12px' }}>{record.phone}</Text>
            </Space>
          )}
          {!record.mobile && !record.phone && (
            <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Ort",
      key: "location",
      width: 120,
      render: (_: any, record: IGuest) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '12px' }}>{record.city || '-'}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.country || 'Deutschland'}
          </Text>
        </Space>
      ),
    },
    {
      title: "Buchungen",
      dataIndex: "total_bookings",
      key: "total_bookings",
      width: 100,
      align: "center" as const,
      render: (value: number) => (
        <Tag color="blue" icon={<CalendarOutlined />}>
          {value || 0}
        </Tag>
      ),
    },
    {
      title: "Umsatz",
      key: "revenue",
      width: 160,
      align: "right" as const,
      render: (_: any, record: IGuest) => (
        <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
          <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
            € {((record.paid_revenue || 0) + (record.pending_revenue || 0)).toFixed(2)}
          </Text>
          <div style={{ fontSize: '11px' }}>
            <Text style={{ color: '#52c41a' }}>
              € {(record.paid_revenue || 0).toFixed(2)} bezahlt
            </Text>
          </div>
          {(record.pending_revenue || 0) > 0 && (
            <div style={{ fontSize: '11px' }}>
              <Text style={{ color: '#fa8c16' }}>
                € {(record.pending_revenue || 0).toFixed(2)} offen
              </Text>
            </div>
          )}
        </Space>
      ),
    },
    {
      title: "Letzte Buchung",
      dataIndex: "last_booking",
      key: "last_booking",
      width: 120,
      render: (value: string) => (
        value ? dayjs(value).format('DD.MM.YYYY') : '-'
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_: any, record: IGuest) => {
        const isRecent = record.last_booking && 
          dayjs().diff(dayjs(record.last_booking), 'days') < 90;
        
        return (
          <Tag color={isRecent ? 'green' : 'default'}>
            {isRecent ? 'Aktiv' : 'Inaktiv'}
          </Tag>
        );
      },
    },
    {
      title: "Aktionen",
      dataIndex: "actions",
      key: "actions",
      width: 150,
      render: (_: any, record: IGuest) => (
        <Space>
          <ShowButton hideText size="small" recordItemId={record.id} />
          <EditButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      headerButtons={[
        <Button 
          key="create" 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => navigateWithHash('/gaeste/neu')}
        >
          Neuer Gast
        </Button>
      ]}
    >
      {/* Statistiken */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Gäste gesamt"
                value={statistics.total_guests}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Gesamtumsatz"
                value={statistics.total_revenue}
                prefix={<EuroOutlined />}
                precision={2}
                suffix="€"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Buchungen gesamt"
                value={statistics.total_bookings}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Ø Buchungswert"
                value={statistics.average_booking_value}
                precision={2}
                suffix="€"
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Suchbereich */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Search
            placeholder="Gäste durchsuchen (Name, E-Mail, Telefon, Ort, Firma)..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
          />
        </Col>
        <Col span={12}>
          <Space style={{ float: 'right' }}>
            <Text type="secondary">
              {filteredData.length} von {tableProps.dataSource?.length || 0} Gästen
            </Text>
          </Space>
        </Col>
      </Row>

      {/* Gäste-Tabelle */}
      <Table 
        {...tableProps}
        dataSource={filteredData}
        columns={columns} 
        rowKey="id"
        size="small"
        scroll={{ x: 1100 }}
        pagination={{
          ...tableProps.pagination,
          total: filteredData.length,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} von ${total} Gästen`,
        }}
      />
    </List>
  );
}; 